import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, ArrowUpDown, ArrowDown, ArrowUp, AlertTriangle, CheckCircle2, Clock, FileText, ChevronDown, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loan, LoanStage, Scope, STAGE_LABELS, STAGE_COLORS, filterLoansByStageAndScope, formatCurrency, formatDate, getEventDate } from '../data/loans';
interface LoanFilesPanelProps {
  open: boolean;
  loans: Loan[];
  initialStage: LoanStage | null;
  initialScope: Scope;
  initialSpeedBonusOnly?: boolean;
  onClose: () => void;
}
type SortKey = 'date' | 'amount' | 'borrower' | 'daysInStage' | 'speedBonus';
type SortDir = 'asc' | 'desc';
const LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'Jumbo'] as const;
export function LoanFilesPanel({
  open,
  loans,
  initialStage,
  initialScope,
  initialSpeedBonusOnly = false,
  onClose
}: LoanFilesPanelProps) {
  const LOANS = loans;
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<LoanStage | 'all'>(initialStage ?? 'all');
  const [scope, setScope] = useState<Scope>(initialScope);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [movingFilter, setMovingFilter] = useState<'all' | 'moving' | 'stuck'>('all');
  const [speedBonusOnly, setSpeedBonusOnly] = useState(initialSpeedBonusOnly);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Reset filters when reopened
  useEffect(() => {
    if (open) {
      setStage(initialStage ?? 'all');
      setScope(initialScope);
      setSpeedBonusOnly(initialSpeedBonusOnly);
      setSearch('');
      setTypeFilter([]);
      setMovingFilter('all');
      setSortKey(initialSpeedBonusOnly ? 'speedBonus' : 'date');
      setSortDir('desc');
    }
  }, [open, initialStage, initialScope, initialSpeedBonusOnly]);
  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  const filteredLoans = useMemo(() => {
    let result: Loan[] = LOANS;
    // Speed bonus filter — scope means closing date in that window
    if (speedBonusOnly) {
      result = result.filter(l => l.speedBonus && l.speedBonus > 0);
      result = result.filter(l => {
        const closing = l.events.find(e => e.stage === 'closing');
        if (!closing) return false;
        if (scope === 'today') return closing.date === '2025-05-14';
        if (scope === 'mtd') return closing.date >= '2025-05-01' && closing.date <= '2025-05-14';
        return true;
      });
    } else if (stage !== 'all') {
      result = filterLoansByStageAndScope(result, stage, scope);
    } else if (scope !== 'all') {
      result = result.filter(loan => loan.events.some(e => {
        if (scope === 'today') return e.date === '2025-05-14';
        if (scope === 'mtd') return e.date >= '2025-05-01' && e.date <= '2025-05-14';
        return true;
      }));
    }
    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l => l.borrower.toLowerCase().includes(q) || l.property.toLowerCase().includes(q) || l.id.toLowerCase().includes(q));
    }
    // Type filter
    if (typeFilter.length > 0) {
      result = result.filter(l => typeFilter.includes(l.loanType));
    }
    // Moving filter
    if (movingFilter === 'moving') result = result.filter(l => l.isMoving);
    if (movingFilter === 'stuck') result = result.filter(l => !l.isMoving);
    // Sort
    const sorted = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'date') {
        const aDate = stage !== 'all' ? getEventDate(a, stage as LoanStage) : a.events[a.events.length - 1].date;
        const bDate = stage !== 'all' ? getEventDate(b, stage as LoanStage) : b.events[b.events.length - 1].date;
        cmp = (aDate ?? '').localeCompare(bDate ?? '');
      } else if (sortKey === 'amount') {
        cmp = a.loanAmount - b.loanAmount;
      } else if (sortKey === 'borrower') {
        cmp = a.borrower.localeCompare(b.borrower);
      } else if (sortKey === 'daysInStage') {
        cmp = a.daysInCurrentStage - b.daysInCurrentStage;
      } else if (sortKey === 'speedBonus') {
        cmp = (a.speedBonus ?? 0) - (b.speedBonus ?? 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [loans, stage, scope, search, typeFilter, movingFilter, speedBonusOnly, sortKey, sortDir]);
  const totalPoints = useMemo(() => {
    if (speedBonusOnly) {
      return filteredLoans.reduce((s, l) => s + (l.speedBonus ?? 0), 0);
    }
    return filteredLoans.reduce((sum, l) => {
      if (stage !== 'all') {
        const e = l.events.find(ev => ev.stage === stage);
        return e ? sum + e.points : sum;
      }
      return sum + l.events.reduce((s, e) => s + e.points, 0);
    }, 0);
  }, [filteredLoans, stage, speedBonusOnly]);
  const totalVolume = filteredLoans.reduce((s, l) => s + l.loanAmount, 0);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const toggleType = (type: string) => {
    setTypeFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const clearFilters = () => {
    setSearch('');
    setStage('all');
    setTypeFilter([]);
    setMovingFilter('all');
    setSpeedBonusOnly(false);
  };
  const headerTitle = speedBonusOnly ? 'Speed Bonus Loans' : stage !== 'all' ? `${STAGE_LABELS[stage as LoanStage]}s` : 'All Loan Files';
  const headerEyebrow = speedBonusOnly ? `${scope === 'today' ? 'Today' : scope === 'mtd' ? 'Month to Date' : 'All Time'} · Fast Closes` : `${scope === 'today' ? 'Today' : scope === 'mtd' ? 'Month to Date' : 'All Time'} · Drill-down`;
  return <AnimatePresence data-id="element-364" data-mp="3392cph2hu6fx">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-id="element-365" data-mp="37a497q5104e7" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 280
      }} className="fixed top-0 right-0 bottom-0 w-full md:w-[860px] bg-gray-50 z-50 shadow-2xl flex flex-col" role="dialog" aria-label="Loan files drill-down" data-id="element-366" data-mp="3m0385v4enjuq">
            {/* Header */}
            <div className={`${speedBonusOnly ? 'bg-orange-500' : 'bg-[#0a1e5e]'} text-white px-6 py-4 flex items-center justify-between shrink-0 transition-colors`} data-id="element-367" data-mp="2gy0w5z4ck008">
              <div className="flex items-center gap-3 min-w-0" data-id="element-368" data-mp="1o9mvfblvipkz">
                {speedBonusOnly && <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0" data-id="element-369" data-mp="1jccylaw5nbn4">
                    <Zap className="w-5 h-5" data-id="element-370" data-mp="1y0zno4bl3hrj" />
                  </div>}
                <div className="min-w-0" data-id="element-371" data-mp="1t8gb9aomusob">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${speedBonusOnly ? 'text-orange-100' : 'text-blue-200'}`} data-id="element-372" data-mp="1t7og20yy6wyk">
                    {headerEyebrow}
                  </p>
                  <h2 className="text-2xl font-extrabold tracking-tight truncate" data-id="element-373" data-mp="f20xhu17mywq">
                    {headerTitle}
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors shrink-0" aria-label="Close panel" data-id="element-374" data-mp="ozotenajycgh">
                <X className="w-5 h-5" data-id="element-375" data-mp="xztxookg8uqf" />
              </button>
            </div>

            {/* Summary Strip */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 grid grid-cols-3 gap-4 shrink-0" data-id="element-376" data-mp="12xmzz6imt4lf">
              <div data-id="element-377" data-mp="17s9e3hg3lb97">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-378" data-mp="1cp9izp17o7yq">
                  {speedBonusOnly ? 'Fast Closes' : 'Files'}
                </p>
                <p className="text-2xl font-extrabold text-[#0a1e5e]" data-id="element-379" data-mp="1hm9i1fzn8agc">
                  {filteredLoans.length}
                </p>
              </div>
              <div data-id="element-380" data-mp="3688hcfnu64ew">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-381" data-mp="368rtffqt1yl0">
                  Total Volume
                </p>
                <p className="text-2xl font-extrabold text-[#0a1e5e]" data-id="element-382" data-mp="2x7adpyhb7ywu">
                  {formatCurrency(totalVolume)}
                </p>
              </div>
              <div data-id="element-383" data-mp="2s9s0vx068zjk">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-384" data-mp="2sabmbnp1662c">
                  {speedBonusOnly ? 'Bonus Points' : 'Points'}
                </p>
                <p className={`text-2xl font-extrabold ${speedBonusOnly ? 'text-orange-500' : 'text-green-600'}`} data-id="element-385" data-mp="3kxegy4ql5wnb">
                  +{totalPoints.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Speed Bonus explainer (when active) */}
            {speedBonusOnly && <div className="bg-orange-50 border-b border-orange-200 px-6 py-3 text-xs text-orange-900 flex items-start gap-2 shrink-0" data-id="element-386" data-mp="7qmheijvt8zb">
                <Zap className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" data-id="element-387" data-mp="3r590gsv73dw6" />
                <p data-id="element-388" data-mp="3m5wcg4sl80s0">
                  <strong data-id="element-389" data-mp="3m6furmm5gr6k">Speed Bonus rules:</strong> Doc Out → Close within{' '}
                  <strong data-id="element-390" data-mp="4xj7fiboi13u">14 days = +1,500 pts</strong>, within{' '}
                  <strong data-id="element-391" data-mp="4uwrovemwfaj">21 days = +500 pts</strong>.
                </p>
              </div>}

            {/* Filters */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0" data-id="element-392" data-mp="dx5wd4n81eg0">
              <div className="flex flex-col gap-3" data-id="element-393" data-mp="8z4icy4nk5be">
                <div className="flex flex-col md:flex-row gap-3" data-id="element-394" data-mp="8ykwx7fsmysm">
                  <div className="relative flex-1" data-id="element-395" data-mp="nmph6fuhvidi">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-396" data-mp="npleq32fk51f" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search borrower, property, or loan ID..." className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-397" data-mp="wof7tjgjyda8" />
                  </div>
                  <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-398" data-mp="129xwnsuu69va">
                    {(['today', 'mtd', 'all'] as Scope[]).map(s => <button key={s} onClick={() => setScope(s)} className={`px-3 py-1 text-xs font-bold uppercase rounded-md transition-colors ${scope === s ? 'bg-white text-[#0a1e5e] shadow-sm' : 'text-gray-600 hover:text-[#0a1e5e]'}`} data-id="element-399" data-mp="skmtemumrzfb">
                        {s === 'today' ? 'Today' : s === 'mtd' ? 'MTD' : 'All'}
                      </button>)}
                  </div>
                </div>

                {/* Speed Bonus toggle */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-400" data-mp="ekc1rhlhygt3">
                  <button onClick={() => setSpeedBonusOnly(!speedBonusOnly)} className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border transition-colors ${speedBonusOnly ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'}`} data-id="element-401" data-mp="2gaasecxfmlw6">
                    <Zap className="w-3.5 h-3.5" data-id="element-402" data-mp="2z7vjzrrkzykh" />
                    Speed Bonus Only
                  </button>
                </div>

                {/* Stage Pills */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-403" data-mp="2q7yp38elm3gz">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1" data-id="element-404" data-mp="2qabhkfhzfw8a">
                    Stage:
                  </span>
                  <button onClick={() => setStage('all')} disabled={speedBonusOnly} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${speedBonusOnly ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : stage === 'all' ? 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-405" data-mp="1etbnpffzl9c5">
                    All
                  </button>
                  {(Object.keys(STAGE_LABELS) as LoanStage[]).map(s => {
                const c = STAGE_COLORS[s];
                const active = stage === s;
                return <button key={s} onClick={() => setStage(s)} disabled={speedBonusOnly} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${speedBonusOnly ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : active ? `${c.bg} ${c.text} ${c.border}` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-406" data-mp="1nel7bmh7t26l">
                        {STAGE_LABELS[s]}
                      </button>;
              })}
                </div>

                {/* Type + Moving */}
                <div className="flex flex-wrap gap-2 items-center" data-id="element-407" data-mp="22hkwq8ve0mfj">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mr-1" data-id="element-408" data-mp="22gryjwahv590">
                    Type:
                  </span>
                  {LOAN_TYPES.map(t => {
                const active = typeFilter.includes(t);
                return <button key={t} onClick={() => toggleType(t)} className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-409" data-mp="15svg0lvhxzv5">
                        {t}
                      </button>;
              })}
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-3 mr-1" data-id="element-410" data-mp="2xq4yav76h774">
                    Status:
                  </span>
                  {(['all', 'moving', 'stuck'] as const).map(m => <button key={m} onClick={() => setMovingFilter(m)} className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${movingFilter === m ? m === 'stuck' ? 'bg-red-100 text-red-700 border-red-200' : m === 'moving' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-411" data-mp="2or9wrwf2wla5">
                      {m === 'all' ? 'All' : m === 'moving' ? 'Moving' : 'Not Moving'}
                    </button>)}
                  {(search || stage !== 'all' || typeFilter.length > 0 || movingFilter !== 'all' || speedBonusOnly) && <button onClick={clearFilters} className="ml-auto text-xs font-bold text-blue-600 hover:text-blue-800 underline" data-id="element-412" data-mp="1cz26blr3b35u">
                      Clear filters
                    </button>}
                </div>
              </div>
            </div>

            {/* Sort Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-2 shrink-0 hidden md:grid grid-cols-12 gap-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-413" data-mp="t7dvjywnz606">
              <button onClick={() => toggleSort('borrower')} className="col-span-4 text-left flex items-center gap-1 hover:text-[#0a1e5e]" data-id="element-414" data-mp="k9bz7wnvl1j8">
                Borrower / Property
                <SortIcon active={sortKey === 'borrower'} dir={sortDir} data-id="element-415" data-mp="2emti36dc5a7x" />
              </button>
              <div className="col-span-2" data-id="element-416" data-mp="2m5at5h0v0x30">
                {speedBonusOnly ? 'Bonus' : 'Stage'}
              </div>
              <button onClick={() => toggleSort('amount')} className="col-span-2 text-right flex items-center gap-1 justify-end hover:text-[#0a1e5e]" data-id="element-417" data-mp="2r2axnni29rs0">
                Amount
                <SortIcon active={sortKey === 'amount'} dir={sortDir} data-id="element-418" data-mp="236yitry7ph5u" />
              </button>
              <button onClick={() => toggleSort('date')} className="col-span-2 text-left flex items-center gap-1 hover:text-[#0a1e5e]" data-id="element-419" data-mp="285jj1rdgs0ua">
                {speedBonusOnly ? 'Close Date' : 'Stage Date'}
                <SortIcon active={sortKey === 'date'} dir={sortDir} data-id="element-420" data-mp="2h79ct3utjbv8" />
              </button>
              <button onClick={() => toggleSort(speedBonusOnly ? 'speedBonus' : 'daysInStage')} className="col-span-2 text-right flex items-center gap-1 justify-end hover:text-[#0a1e5e]" data-id="element-421" data-mp="3oj9ob1gbkv5i">
                {speedBonusOnly ? 'Speed' : 'Days'}
                <SortIcon active={sortKey === (speedBonusOnly ? 'speedBonus' : 'daysInStage')} dir={sortDir} data-id="element-422" data-mp="dkiw385mk9mw" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-3" data-id="element-423" data-mp="1178zactpma93">
              {filteredLoans.length === 0 ? <div className="text-center py-16" data-id="element-424" data-mp="w8nv62bc76fd">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-425" data-mp="1b66m9bbhlvpz" />
                  <p className="text-gray-500 font-semibold" data-id="element-426" data-mp="1b43scg77rifz">
                    {speedBonusOnly ? 'No fast closes in this timeframe' : 'No loan files match these filters'}
                  </p>
                  <button onClick={clearFilters} className="mt-3 text-sm text-blue-600 font-bold hover:underline" data-id="element-427" data-mp="37zrvcv9ovxck">
                    Clear filters
                  </button>
                </div> : <ul className="space-y-2" data-id="element-428" data-mp="1dr5tub3rjvkg">
                  {filteredLoans.map(loan => <LoanRow key={loan.id} loan={loan} contextStage={stage !== 'all' && !speedBonusOnly ? stage as LoanStage : undefined} speedBonusMode={speedBonusOnly} data-id="element-429" data-mp="1ippjomlh9ri3" />)}
                </ul>}
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function SortIcon({
  active,
  dir
}: {
  active: boolean;
  dir: SortDir;
}) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" data-id="element-430" data-mp="2u5ny75zfd5qp" />;
  return dir === 'asc' ? <ArrowUp className="w-3 h-3" data-id="element-431" data-mp="1jwov9t3nkp6" /> : <ArrowDown className="w-3 h-3" data-id="element-432" data-mp="1iv8cptbe602" />;
}
function LoanRow({
  loan,
  contextStage,
  speedBonusMode
}: {
  loan: Loan;
  contextStage?: LoanStage;
  speedBonusMode?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const c = STAGE_COLORS[loan.currentStage];
  // Compute days from doc out → close for speed bonus context
  const docOut = loan.events.find(e => e.stage === 'doc_out');
  const closing = loan.events.find(e => e.stage === 'closing');
  const docToCloseDays = docOut && closing ? Math.round((new Date(closing.date).getTime() - new Date(docOut.date).getTime()) / 86400000) : null;
  const displayDate = speedBonusMode ? closing?.date : contextStage ? getEventDate(loan, contextStage) : loan.events[loan.events.length - 1].date;
  return <li className={`bg-white border ${expanded ? 'border-blue-300 shadow-md' : 'border-gray-200'} rounded-lg transition-all`} data-id="element-433" data-mp="2w21jqeljhl04">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-3 md:grid md:grid-cols-12 md:gap-3 md:items-center flex flex-col gap-2 hover:bg-gray-50/50 rounded-lg" data-id="element-434" data-mp="2shht6tgubw23">
        {/* Borrower */}
        <div className="col-span-4 min-w-0" data-id="element-435" data-mp="32foy2dxuunnq">
          <p className="font-bold text-[#0a1e5e] text-sm truncate" data-id="element-436" data-mp="2xhdnu23neb4z">
            {loan.borrower}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-437" data-mp="29r05526ydon2">{loan.property}</p>
          <p className="text-[10px] text-gray-400 font-mono" data-id="element-438" data-mp="29p6uzcx4smab">
            {loan.id} · {loan.loanType}
          </p>
        </div>

        {/* Bonus pill OR Stage pill */}
        <div className="col-span-2" data-id="element-439" data-mp="2jixl0m8wp4s4">
          {speedBonusMode ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700" data-id="element-440" data-mp="2epl6x61usgdm">
              <Zap className="w-3 h-3" data-id="element-441" data-mp="1qz79f00i1pbn" />+{loan.speedBonus} pts
            </span> : <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`} data-id="element-442" data-mp="1m1p2ff61lk6a">
              {loan.isMoving ? <CheckCircle2 className="w-3 h-3" data-id="element-443" data-mp="3r1qbcgs1i92x" /> : <AlertTriangle className="w-3 h-3" data-id="element-444" data-mp="3vv2q7z6x31ih" />}
              {STAGE_LABELS[loan.currentStage]}
            </span>}
        </div>

        {/* Amount */}
        <div className="col-span-2 md:text-right" data-id="element-445" data-mp="335vqwxzn27w6">
          <p className="text-sm font-bold text-[#0a1e5e]" data-id="element-446" data-mp="3376uoxt33fna">
            {formatCurrency(loan.loanAmount)}
          </p>
        </div>

        {/* Date */}
        <div className="col-span-2" data-id="element-447" data-mp="3c5r8bkjsez4n">
          <p className="text-xs text-gray-600" data-id="element-448" data-mp="3c4pkefhoai5i">
            {displayDate ? formatDate(displayDate) : '—'}
          </p>
        </div>

        {/* Days / Speed */}
        <div className="col-span-2 md:text-right flex md:block items-center justify-between" data-id="element-449" data-mp="rgl03s3aafao">
          {speedBonusMode && docToCloseDays !== null ? <p className="text-sm font-bold text-orange-600" data-id="element-450" data-mp="wgpkht7bbu66">
              {docToCloseDays}d
              <span className="text-[10px] text-gray-400 font-medium ml-1" data-id="element-451" data-mp="16kxj6ihmzjsz">
                doc → close
              </span>
            </p> : <p className={`text-sm font-bold ${loan.daysInCurrentStage > 5 && !loan.isMoving ? 'text-red-600' : 'text-gray-700'}`} data-id="element-452" data-mp="wryku8wc8ew6">
              {loan.daysInCurrentStage}d
              <span className="text-[10px] text-gray-400 font-medium ml-1" data-id="element-453" data-mp="ntd3ui6il7xi">
                in stage
              </span>
            </p>}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-454" data-mp="ivldhsliptvf" />
        </div>
      </button>

      {expanded && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 overflow-hidden" data-id="element-455" data-mp="9un9hab2xmcu">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-456" data-mp="2babb8vjyiikk">
            Stage Timeline
          </p>
          <ol className="relative space-y-2 pl-4 border-l-2 border-gray-200" data-id="element-457" data-mp="2293nh7f2h04q">
            {loan.events.map(ev => {
          const ec = STAGE_COLORS[ev.stage];
          return <li key={ev.stage} className="relative" data-id="element-458" data-mp="26cji5y7ksqes">
                  <span className={`absolute -left-[19px] top-1.5 w-3 h-3 rounded-full ${ec.bg} ${ec.text} border-2 border-white`} data-id="element-459" data-mp="1siifwuhf0l9o"></span>
                  <div className="flex items-center justify-between" data-id="element-460" data-mp="1ilvvg6t03w86">
                    <div data-id="element-461" data-mp="1nk762jr4earg">
                      <p className="text-xs font-bold text-[#0a1e5e]" data-id="element-462" data-mp="1njfls52z3zgh">
                        {STAGE_LABELS[ev.stage]}
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-463" data-mp="19miw46ylco16">
                        {formatDate(ev.date)}
                      </p>
                    </div>
                    <span className={`text-xs font-bold ${ec.text}`} data-id="element-464" data-mp="uysb16pp65yt">
                      +{ev.points} pts
                    </span>
                  </div>
                </li>;
        })}
          </ol>
          {loan.speedBonus ? <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-2.5 flex items-center gap-2" data-id="element-465" data-mp="13w2o8xm2s56d">
              <Zap className="w-4 h-4 text-orange-500 shrink-0" data-id="element-466" data-mp="1dwbm0fvptynv" />
              <p className="text-xs font-semibold text-orange-700" data-id="element-467" data-mp="1duidsw5gf6dr">
                Speed Bonus: <strong data-id="element-468" data-mp="18wz0bygkyxmv">+{loan.speedBonus} pts</strong>
                {docToCloseDays !== null && <span className="text-orange-600/80" data-id="element-469" data-mp="1nlv7zueq68my">
                    {' '}
                    · Doc Out to Close in {docToCloseDays} days
                  </span>}
              </p>
            </div> : null}
          {!loan.isMoving && <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2" data-id="element-470" data-mp="1rp19koqw7bn3">
              <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-471" data-mp="pbn02ae8nlev" />
              <p className="text-xs font-semibold text-red-700" data-id="element-472" data-mp="u9ey7m5h83ri">
                File not moving — losing 50 pts/day until status changes
              </p>
            </div>}
        </motion.div>}
    </li>;
}